import { notification } from 'antd';
import { NotificationProps } from './types';
import i18next from 'i18next';

interface NetworkMonitorOptions {
  enableNotifications?: boolean;
  notificationDuration?: number;
  debounceDelay?: number;
}

class NetworkMonitor {
  private isOnline: boolean = navigator.onLine;
  private lastNotificationType: 'online' | 'offline' | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private options: Required<NetworkMonitorOptions>;

  constructor(options: NetworkMonitorOptions = {}) {
    this.options = {
      enableNotifications: true,
      notificationDuration: 4.5,
      debounceDelay: 1000,
      ...options,
    };

    this.init();
  }

  private init() {
    // Set initial state
    this.isOnline = navigator.onLine;

    // Add event listeners
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Cleanup on page unload
    window.addEventListener('beforeunload', this.cleanup.bind(this));
  }

  private handleOnline = () => {
    this.updateStatus(true);
  };

  private handleOffline = () => {
    this.updateStatus(false);
  };

  private updateStatus(isOnline: boolean) {
    if (this.isOnline === isOnline) return; // No change

    this.isOnline = isOnline;
    this.showNotification(isOnline ? 'online' : 'offline');
  }

  private showNotification(type: 'online' | 'offline') {
    if (!this.options.enableNotifications) return;

    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Close all existing notifications immediately when status changes
    notification.destroy();

    // Debounce the notification
    this.debounceTimer = setTimeout(() => {
      const message =
        type === 'online'
          ? i18next.t('network.connectionRestored', 'Connection restored')
          : i18next.t('network.connectionLost', 'No internet connection');

      const description =
        type === 'online'
          ? i18next.t('network.connectionRestoredDesc', 'Your internet connection has been restored.')
          : i18next.t('network.connectionLostDesc', 'Please check your internet connection and try again.');

      // For offline: persistent notification (duration: 0 = never auto-close)
      // For online: auto-hide after specified duration
      const duration = type === 'online' ? this.options.notificationDuration : 0;

      notification[type === 'online' ? 'success' : 'error']({
        ...NotificationProps,
        message,
        description,
        duration,
        placement: 'topRight',
        closable: type === 'online', // Only closable when online
      });

      this.lastNotificationType = type;
    }, this.options.debounceDelay);
  }

  private cleanup() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    // Close all notifications
    notification.destroy();
  }

  public destroy() {
    this.cleanup();
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('beforeunload', this.cleanup);
  }

  public getStatus() {
    return {
      isOnline: this.isOnline,
    };
  }
}

// Create singleton instance
let networkMonitorInstance: NetworkMonitor | null = null;

export const initNetworkMonitor = (options?: NetworkMonitorOptions) => {
  if (networkMonitorInstance) {
    networkMonitorInstance.destroy();
  }

  networkMonitorInstance = new NetworkMonitor(options);
  return networkMonitorInstance;
};

export const getNetworkMonitor = () => networkMonitorInstance;

export const destroyNetworkMonitor = () => {
  if (networkMonitorInstance) {
    networkMonitorInstance.destroy();
    networkMonitorInstance = null;
  }
};
